import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Button, Col, Row, message, Space } from 'antd';
import { SyncOutlined, BellOutlined } from '@ant-design/icons';
import DashboardTabs from '../../components/admin/DashboardTabs';
import StatCard from '../../components/admin/StatCard';
import IntentDistribution from '../../components/admin/IntentDistribution';
import RecentConversations from '../../components/admin/RecentConversations';
import ConversationTrendChart from '../../components/admin/ConversationTrendChart';
import KnowledgeStatus from '../../components/admin/KnowledgeStatus';
import { dashboardApi, BasicStatsResponse, AnalyticsStatsResponse } from '../../services/dashboardService';

import { useAuthStore } from '../../stores/authStore';

const DashboardPage: React.FC = () => {
    const { user } = useAuthStore();
    const [activeTab, setActiveTab] = useState<'basic' | 'analytics'>('basic');
    const [basicData, setBasicData] = useState<BasicStatsResponse | null>(null);
    const [analyticsData, setAnalyticsData] = useState<AnalyticsStatsResponse | null>(null);
    const [loading, setLoading] = useState(true);       // 首次加载 skeleton
    const [refreshing, setRefreshing] = useState(false); // 刷新图标旋转
    const initialLoadDone = useRef(false);

    const fetchBasicStats = useCallback(async () => {
        try {
            const res = await dashboardApi.getBasicStats();
            setBasicData(res);
        } catch (error) {
            console.error('Failed to fetch basic stats', error);
            message.error('获取基础统计数据失败');
        }
    }, []);

    const fetchAnalytics = useCallback(async () => {
        try {
            const res = await dashboardApi.getAnalytics();
            setAnalyticsData(res);
        } catch (error) {
            console.error('Failed to fetch analytics', error);
            message.error('获取运营分析数据失败');
        }
    }, []);

    // 首次/切换 Tab 时显示 skeleton；手动/自动刷新时静默更新
    const loadData = useCallback(async (silent = false) => {
        if (!silent) {
            setLoading(true);
        }
        setRefreshing(true);
        if (activeTab === 'basic') {
            await fetchBasicStats();
        } else {
            await fetchAnalytics();
        }
        setLoading(false);
        setRefreshing(false);
        initialLoadDone.current = true;
    }, [activeTab, fetchBasicStats, fetchAnalytics]);

    useEffect(() => {
        // 切换 Tab 时如果已有数据则静默刷新，否则首次加载
        loadData(initialLoadDone.current);

        // 60秒自动刷新（静默）
        const interval = setInterval(() => {
            loadData(true);
        }, 60000);

        return () => clearInterval(interval);
    }, [activeTab, loadData]);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* 顶部栏 */}
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '0 32px',
                    height: 64,
                    background: '#fff',
                    borderBottom: '1px solid #e4e4e7',
                    flexShrink: 0,
                }}
            >
                <h2 style={{ margin: 0, fontSize: 20, fontWeight: 600, color: '#18181b' }}>
                    总览
                </h2>
                <Space>
                    <BellOutlined style={{ fontSize: 18, color: '#71717a', cursor: 'pointer' }} />
                    <div
                        style={{
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            background: '#3b82f6',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#fff',
                            fontWeight: 600,
                            fontSize: 14,
                        }}
                    >
                        {user?.username?.[0]?.toUpperCase() || 'A'}
                    </div>
                </Space>
            </div>

            <div style={{ flex: 1, padding: 24, overflow: 'auto' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                    <DashboardTabs activeTab={activeTab} onChange={setActiveTab} />
                    <Button
                        type="text"
                        icon={<SyncOutlined spin={refreshing} />}
                        onClick={() => loadData(true)}
                        disabled={refreshing}
                    >
                        刷新
                    </Button>
                </div>

                {activeTab === 'basic' ? (
                    <div className="basic-stats-tab">
                        <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
                            <Col xs={24} sm={12} xl={6}>
                                <StatCard
                                    title="总对话数"
                                    value={basicData?.overview.total_conversations || 0}
                                    trend={basicData?.overview.conversation_trend || "0%"}
                                    trendType={basicData?.overview.conversation_trend?.startsWith('-') ? 'down' : 'up'}
                                    icon="message-circle"
                                    iconColor="#2563EB"
                                    loading={loading}
                                />
                            </Col>
                            <Col xs={24} sm={12} xl={6}>
                                <StatCard
                                    title="AI解决率"
                                    value={`${basicData?.overview.ai_resolve_rate || 0}%`}
                                    trend={basicData?.overview.resolve_trend || "0%"}
                                    trendType={basicData?.overview.resolve_trend?.startsWith('-') ? 'down' : 'up'}
                                    icon="zap"
                                    iconColor="#F59E0B"
                                    loading={loading}
                                />
                            </Col>
                            <Col xs={24} sm={12} xl={6}>
                                <StatCard
                                    title="平均响应"
                                    value={`${basicData?.overview.avg_response_time || 0}s`}
                                    trend={basicData?.overview.response_trend || "0s"}
                                    trendType={basicData?.overview.response_trend?.startsWith('+') ? 'down' : 'up'} // 响应时间增加是下降趋势（变差）
                                    icon="clock"
                                    iconColor="#8B5CF6"
                                    loading={loading}
                                />
                            </Col>
                            <Col xs={24} sm={12} xl={6}>
                                <StatCard
                                    title="索引失败任务"
                                    value={basicData?.overview.failed_tasks || 0}
                                    trend="需重新处理"
                                    trendType={basicData?.overview.failed_tasks && basicData.overview.failed_tasks > 0 ? 'warning' : 'up'}
                                    icon="alert-circle"
                                    iconColor="#EF4444"
                                    loading={loading}
                                />
                            </Col>
                        </Row>

                        <Row gutter={[24, 24]}>
                            <Col xs={24} lg={10}>
                                <IntentDistribution data={basicData?.intent_distribution || []} loading={loading} />
                            </Col>
                            <Col xs={24} lg={14}>
                                <RecentConversations data={basicData?.recent_conversations || []} loading={loading} />
                            </Col>
                        </Row>
                    </div>
                ) : (
                    <div className="analytics-tab">
                        <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
                            <Col xs={24} sm={12} xl={6}>
                                <StatCard
                                    title="知识库文档"
                                    value={analyticsData?.stats.knowledge_docs || 0}
                                    trend={analyticsData?.stats.knowledge_trend || "0"}
                                    trendType="up"
                                    icon="database"
                                    iconColor="#10B981"
                                    loading={loading}
                                />
                            </Col>
                            <Col xs={24} sm={12} xl={6}>
                                <StatCard
                                    title="FAQ数量"
                                    value={analyticsData?.stats.faq_count || 0}
                                    trend={analyticsData?.stats.faq_trend || "0"}
                                    trendType="up"
                                    icon="help-circle"
                                    iconColor="#8B5CF6"
                                    loading={loading}
                                />
                            </Col>
                            <Col xs={24} sm={12} xl={6}>
                                <StatCard
                                    title="今日新用户"
                                    value={analyticsData?.stats.today_users || 0}
                                    trend={analyticsData?.stats.users_trend || "0"}
                                    trendType="up"
                                    icon="user-plus"
                                    iconColor="#F59E0B"
                                    loading={loading}
                                />
                            </Col>
                            <Col xs={24} sm={12} xl={6}>
                                <StatCard
                                    title="今日对话"
                                    value={analyticsData?.stats.today_conversations || 0}
                                    trend={analyticsData?.stats.conversations_trend || "0"}
                                    trendType="up"
                                    icon="message-square"
                                    iconColor="#2563EB"
                                    loading={loading}
                                />
                            </Col>
                        </Row>

                        <Row gutter={[24, 24]}>
                            <Col xs={24} lg={16}>
                                <ConversationTrendChart data={analyticsData?.conversation_trend_7d || []} loading={loading} />
                            </Col>
                            <Col xs={24} lg={8}>
                                <KnowledgeStatus
                                    data={analyticsData?.knowledge_status || { indexed: 0, processing: 0, failed: 0 }}
                                    loading={loading}
                                />
                            </Col>
                        </Row>
                    </div>
                )}
            </div>
        </div>
    );
};

export default DashboardPage;